import React from 'react';
import { createRoot } from 'react-dom/client';
import { config } from './config/config';
import { SMLAppNavigation } from './routing/sml/SMLAppNavigation';
import { Sentry } from './modules/errorBoundary/sentry';
import './assets/styles/sml/appStyle.scss';
import { AppSML } from './modules/app/views/AppSML';
import { applySMLThemeToDocument, getInitialSMLTheme } from './modules/music24/theme/smlTheme';
import { captureAttribution } from './modules/analytics/attribution/attributionStore';
import { captureSessionAtBoot } from './modules/music24/whatsNew/sessionAtBoot';
import { guardDomMutations } from './modules/errorBoundary/guardDomMutations';
import { webBeforeSend } from './modules/errorBoundary/sentryFilters';

console.log(`%c -- SmartLinks App --`, 'background: #6417fb; color: white;');
if (config.sentryDNS !== '') {
  Sentry.init({
    dsn: config.sentryDNS,
    environment: config.environment,
    maxBreadcrumbs: 50,
    ignoreErrors: [
      'FYMOperationalError', // these are for us
      'UserCancelledError',
      // The link scanner of Outlook / Microsoft Safe Links opens a mailed link in a headless browser
      // and rejects a promise with this text (MUSIC24-WEB-9). No stack, no code of ours. The website
      // ignores it too (music24-website/app/src/utils/sentryFilters.ts).
      'Object Not Found Matching Id:',
      // The Facebook Android in-app browser's own script (`navigation_performance_logger_android`)
      // calls its native bridge on unload after the WebView dropped it (MUSIC24-WEB-G). The website
      // ignores it too.
      'Error invoking postMessage: Java object is gone',
    ],
    beforeSend: webBeforeSend,
  });
}

declare global {
  interface Window {
    analytics: any;
    clarity: any;
    dataLayer: any;
    gtag: any;
    Realm: any;
    require: any;
    isOnServer: boolean;
    plausible?: ((event: string, options?: { u?: string; props?: Record<string, unknown> }) => void) & {
      q?: unknown[];
    };
  }
}

// A page translator moves our text nodes; keep React from crashing on them (guardDomMutations).
guardDomMutations();
// Apply the Music24 theme before the React tree renders: the stored choice, else the device color
// scheme (`system`, the default on every client). For the paint before this script runs,
// public/index.html sets data-theme="system", and _tokens.scss maps it to the device scheme. This
// call replaces it with the resolved "light" or "dark".
applySMLThemeToDocument(getInitialSMLTheme());
// Keep the campaign parameters and the ad click ids of this arrival before a route changes the URL.
captureAttribution();
// Note whether a session existed at page load, before a sign-in can add one (What's new: a fresh
// browser shows no release notes).
captureSessionAtBoot();
const container = document.getElementById('root');
if (!container) {
  throw new Error('Cannot find root element');
}
const root = createRoot(container);
root.render(
  <AppSML>
    <SMLAppNavigation />
  </AppSML>
);

console.info(`Environment: ${config.environment}`);
